(() => {
  'use strict';

  if (window.olitansPhase4ShellUI || !document.body) return;

  const body = document.body;
  const mobileQuery = window.matchMedia('(max-width: 1024px)');
  const desktopQuery = window.matchMedia('(min-width: 1025px)');
  const mobileTriggerSelector = '.section-header-mobile [data-mobile-menu]';
  const mobileDrawerSelector = '#navigation-mobile';
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let menuWasOpen = false;
  let mobileMenuTrigger = null;
  let menuFocusFrame = null;
  let desktopPanelId = 0;
  let mobilePanelId = 0;
  let mobileDrawerObserver = null;
  let observedMobileDrawer = null;

  const safeFocus = (element) => {
    if (!element?.isConnected) return;

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  };

  const isVisible = (element) => {
    if (!element || element.hidden || element.closest('[inert]')) return false;
    if (typeof element.getClientRects !== 'function') return true;
    return element.getClientRects().length > 0;
  };

  const getFocusable = (container) => Array.from(container?.querySelectorAll(focusableSelector) || [])
    .filter(isVisible);

  const getMobileDrawer = () => document.querySelector(mobileDrawerSelector);

  const getMobileAccordionTrigger = (item) => item?.querySelector([
    ':scope > .menu_mobile_link',
    ':scope > .site-nav-list > .site-nav-title',
    ':scope > .site-nav-list > .menu_mobile_link_2'
  ].join(','));

  const getMobileAccordionPanel = (item) => {
    if (!item) return null;

    return Array.from(item.children || []).find((child) => child.matches?.([
      '.menu-dropdown',
      '.menu-lv__dropdownmenu',
      '.list-menu--disclosure-1',
      '.list-menu--disclosure-2',
      '.site-nav-sub'
    ].join(','))) || item.querySelector([
      ':scope > .site-nav-list > .site-nav-sub',
      ':scope > .site-nav-list > .menu-lv__dropdownmenu-2'
    ].join(','));
  };

  const initializeMobileAccordions = () => {
    const drawer = getMobileDrawer();
    if (!drawer) return;

    drawer.querySelectorAll('.menu_mobile_link, .menu_mobile_link_2, .site-nav-title').forEach((trigger) => {
      const item = trigger.closest('.menu-lv-item, .site-nav');
      const panel = getMobileAccordionPanel(item);
      if (!item || !panel) return;

      if (!panel.id) {
        mobilePanelId += 1;
        panel.id = `olitans-mobile-menu-panel-${mobilePanelId}`;
      }

      const expanded = item.classList.contains('is-accordion-open') || item.classList.contains('is-open');
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', String(expanded));
      panel.setAttribute('aria-hidden', String(!expanded));
    });
  };

  const observeMobileDrawer = () => {
    const drawer = getMobileDrawer();
    if (!drawer || drawer === observedMobileDrawer) return;

    mobileDrawerObserver?.disconnect();
    observedMobileDrawer = drawer;
    mobileDrawerObserver = new MutationObserver(initializeMobileAccordions);
    mobileDrawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
  };

  const collapseCurrentMobileLevel = () => {
    const drawer = getMobileDrawer();
    if (!drawer) return false;

    const activeItem = document.activeElement?.closest?.('.is-accordion-open');
    const openItems = Array.from(drawer.querySelectorAll('.is-accordion-open'));
    const accordionItem = activeItem && drawer.contains(activeItem) ? activeItem : openItems.at(-1);

    if (accordionItem) {
      const trigger = getMobileAccordionTrigger(accordionItem);
      accordionItem.classList.remove('is-accordion-open');
      trigger?.setAttribute('aria-expanded', 'false');
      safeFocus(trigger);
      return true;
    }

    const legacyItem = document.activeElement?.closest?.('.menu-lv-item.is-open, .site-nav.is-open')
      || Array.from(drawer.querySelectorAll('.menu-lv-item.is-open, .site-nav.is-open')).at(-1);
    const legacyBack = legacyItem?.querySelector('.nav-title-mobile');

    if (legacyBack) {
      const trigger = getMobileAccordionTrigger(legacyItem);
      legacyBack.click();
      trigger?.setAttribute('aria-expanded', 'false');
      safeFocus(trigger);
      return true;
    }

    return false;
  };

  const syncMobileMenu = () => {
    const drawer = getMobileDrawer();
    const triggers = document.querySelectorAll(mobileTriggerSelector);
    const isOpen = mobileQuery.matches && body.classList.contains('menu_open');

    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-controls', drawer?.id || 'navigation-mobile');
      trigger.setAttribute('aria-expanded', String(isOpen));
      trigger.classList.toggle('is-open', isOpen);
    });

    if (drawer) {
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-label', triggers[0]?.getAttribute('aria-label') || 'Menu');
      drawer.setAttribute('aria-hidden', String(!isOpen));
      drawer.toggleAttribute('inert', !isOpen);
    }

    observeMobileDrawer();
    initializeMobileAccordions();

    if (isOpen && !menuWasOpen) {
      if (!mobileMenuTrigger?.isConnected) {
        mobileMenuTrigger = Array.from(triggers).find((trigger) => trigger === document.activeElement) || triggers[0] || null;
      }

      window.cancelAnimationFrame(menuFocusFrame);
      menuFocusFrame = window.requestAnimationFrame(() => {
        const closeButton = drawer?.querySelector('[data-menu-close-sidebar], .halo-sidebar-close');
        safeFocus(closeButton || getFocusable(drawer)[0]);
      });
    } else if (!isOpen && menuWasOpen) {
      window.cancelAnimationFrame(menuFocusFrame);
      safeFocus(mobileMenuTrigger);
    }

    menuWasOpen = isOpen;
  };

  const getDirectDesktopPanel = (item) => {
    if (!item) return null;

    return Array.from(item.children || []).find((child) => child.matches?.('.menu-dropdown, .header__submenu'))
      || item.querySelector(':scope > .site-nav-list-dropdown > .header__submenu');
  };

  const getDirectDesktopTrigger = (item) => item?.querySelector([
    ':scope > .menu-lv-1__action',
    ':scope > .menu-lv-2__action',
    ':scope > .site-nav-list-dropdown > .menu-lv-2__action'
  ].join(','));

  const setDesktopMenuExpanded = (item, expanded, clearCollapsed = false) => {
    const trigger = getDirectDesktopTrigger(item);
    const panel = getDirectDesktopPanel(item);
    if (!trigger || !panel) return;

    if (clearCollapsed) item.classList.remove('olitans-shell-menu-collapsed');
    trigger.setAttribute('aria-expanded', String(expanded));
    panel.setAttribute('aria-hidden', String(!expanded));
  };

  const initializeDesktopMenus = () => {
    document.querySelectorAll('.section-header-navigation .menu-lv-item.dropdown').forEach((item) => {
      const trigger = getDirectDesktopTrigger(item);
      const panel = getDirectDesktopPanel(item);
      if (!trigger || !panel) return;

      if (!panel.id) {
        desktopPanelId += 1;
        panel.id = `olitans-header-panel-${desktopPanelId}`;
      }

      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-haspopup', 'true');
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      if (!panel.hasAttribute('aria-hidden')) panel.setAttribute('aria-hidden', 'true');

      if (desktopQuery.matches && !item.contains(document.activeElement)) {
        item.classList.remove('olitans-shell-menu-collapsed');
        trigger.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
      }

      if (item.dataset.olitansShellMenuReady === 'true') return;
      item.dataset.olitansShellMenuReady = 'true';

      item.addEventListener('pointerenter', () => {
        if (!desktopQuery.matches) return;
        setDesktopMenuExpanded(item, true, true);
      });

      item.addEventListener('pointerleave', () => {
        if (!desktopQuery.matches) return;
        window.requestAnimationFrame(() => {
          const keyboardFocusRemains = item.contains(document.activeElement)
            && !item.classList.contains('olitans-shell-menu-collapsed');
          setDesktopMenuExpanded(item, keyboardFocusRemains);
        });
      });

      item.addEventListener('focusin', () => {
        if (!desktopQuery.matches) return;
        setDesktopMenuExpanded(item, true, true);
      });

      item.addEventListener('focusout', () => {
        if (!desktopQuery.matches) return;
        window.requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) {
            item.classList.remove('olitans-shell-menu-collapsed');
            setDesktopMenuExpanded(item, false);
          }
        });
      });

      item.addEventListener('keydown', (event) => {
        if (!desktopQuery.matches) return;

        if (event.key === 'ArrowDown' && event.target === trigger) {
          const firstPanelControl = getFocusable(panel)[0];
          if (!firstPanelControl) return;
          event.preventDefault();
          setDesktopMenuExpanded(item, true, true);
          safeFocus(firstPanelControl);
          return;
        }

        if (event.key !== 'Escape' || !item.contains(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        safeFocus(trigger);
        item.classList.add('olitans-shell-menu-collapsed');
        trigger.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
      });
    });
  };

  const refresh = () => {
    initializeDesktopMenus();
    syncMobileMenu();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(mobileTriggerSelector);
    if (trigger) mobileMenuTrigger = trigger;
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!mobileQuery.matches || !body.classList.contains('menu_open')) return;

    const drawer = getMobileDrawer();
    if (!drawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();

      if (collapseCurrentMobileLevel()) return;

      const closeButton = drawer.querySelector('[data-menu-close-sidebar], .halo-sidebar-close');
      if (closeButton) closeButton.click();
      else body.classList.remove('menu_open');
      return;
    }

    if (event.key !== 'Tab') return;

    const controls = getFocusable(drawer);
    if (!controls.length) return;

    const first = controls[0];
    const last = controls.at(-1);

    if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
      event.preventDefault();
      safeFocus(last);
    } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
      event.preventDefault();
      safeFocus(first);
    }
  }, true);

  const bodyObserver = new MutationObserver(syncMobileMenu);
  bodyObserver.observe(body, { attributes: true, attributeFilter: ['class'] });

  document.addEventListener('shopify:section:load', refresh);
  mobileQuery.addEventListener?.('change', refresh);
  desktopQuery.addEventListener?.('change', refresh);

  window.olitansPhase4ShellUI = {
    refresh,
    syncMobileMenu
  };

  refresh();
})();
